import { useCallback } from 'react';
import { Flex } from '@mantine/core';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useConfirm } from '@/components/Modals/useConfirm';
import { useClientTerminology } from '@/views/list/NetworkTerms';

const MAX_LIST_WARNING_COUNT = 10_000;

interface UseAddToListOptions {
  /** Total number of items that would be added */
  total: number | undefined;
  /** Callback to open the list selector modal */
  openModal: () => void;
}

/**
 * Encapsulates the "Add to project" flow:
 * 1. Fire a GA event
 * 2. If total > 10 000, show a confirmation warning (with inverted confirm/cancel)
 * 3. Open the list selector modal
 */
export function useAddToList({ total, openModal }: UseAddToListOptions) {
  const confirm = useConfirm();
  const intl = useClientTerminology();

  const handleAddToList = useCallback(async () => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.clickedAddToProject,
      },
    });

    if (total != null && total > MAX_LIST_WARNING_COUNT) {
      const result = await confirm({
        titleId: 'customList.addToList',
        children: (
          <Flex fz="sm" gap="xs" direction="column">
            <strong>
              {intl.formatMessage(
                { id: 'customList.addALotToListAreYouSure' },
                { count: total }
              )}
            </strong>
            <span>
              {intl.formatMessage({ id: 'customList.addALotToListMessage' })}
            </span>
          </Flex>
        ),
        primaryLabelId: 'customList.addALotToListCancel',
        secondaryLabelId: 'customList.addALotToListConfirm',
      });

      // Logic is inverted: the "confirm" button actually cancels the add,
      // because we want to encourage users _not_ to add too many items.
      if (result.confirmed) {
        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.projects.clickOut.tooManySpansRevert,
          },
        });
        return;
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.projects.clickOut.tooManySpansContinue,
          },
        });
      }
    }

    openModal();
  }, [total, confirm, intl, openModal]);

  return handleAddToList;
}
